import { useCallback, useEffect, useState } from 'react';
import { Form, Input, message, Spin, Typography, InputNumber, Alert, Divider, Checkbox } from 'antd';
import { Banner } from 'components/ui/Banner';
import Card from 'components/Card/Card';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import Select, { AsyncSelect } from 'components/Select';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { DatePicker } from 'components/ui/DatePicker';
import PhoneNumber from 'components/PhoneNumber';
import { Document } from '@carbon/icons-react';
import { getPhoneNumberValidator } from 'utils';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppSpouseEmployerDetails({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form] = Form.useForm();
  const payStubsAvailableFormValue = Form.useWatch('payStubsAvailable', form);
  const employerCountFormValue = Form.useWatch('employerCount', form);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        await familyService.addUpdateAdult(household?.id, application?.additionalParents?.[0]?.id ?? null, {
          ...application?.additionalParents?.[0],
          employmentDetails: {
            ...application?.additionalParents?.[0]?.employmentDetails,
            payStubsAvailable: values.payStubsAvailable,
            hoursPerWeek: values.hoursPerWeek,
            employerCount: values.employerCount?.id,
            // Only save payFrequency if payStubsAvailable is true and employerCount is 1, otherwise clear it
            payFrequency:
              values.payStubsAvailable === true && values.employerCount?.id === 1 ? values.payFrequency : null,
            companyName: values.companyName,
            jobTitle: values.jobTitle,
            startDate: values.startDate,
            employerState: values.employerState,
            supervisorName: values.supervisorName,
            supervisorPhone: values.supervisorPhone,
            certificationAccepted: values.certificationAccepted,
            certificationSigned: values.certificationSigned,
          },
        });

        const _newApplication = await familyService.getApplication(application?.id);

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic?.noticeError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.additionalParents?.[0]?.employmentDetails) {
      const employmentDetails = application.additionalParents[0].employmentDetails;

      form.setFieldsValue({
        payStubsAvailable: employmentDetails.payStubsAvailable,
        hoursPerWeek: employmentDetails.hoursPerWeek,
        employerCount: { id: employmentDetails.employerCount },
        payFrequency: employmentDetails.payFrequency,
        companyName: employmentDetails.companyName,
        jobTitle: employmentDetails.jobTitle,
        startDate: employmentDetails.startDate,
        employerState: employmentDetails.employerState,
        supervisorName: employmentDetails.supervisorName,
        supervisorPhone: employmentDetails.supervisorPhone,
        certificationAccepted: employmentDetails.certificationAccepted,
        certificationSigned: employmentDetails.certificationSigned,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Spouse Employer Information" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Tell us about ${getOtherAdultFirstName(application?.additionalParents?.[0])}'s job`
                : 'Tell us about your job'
            }
            className="my-12"
          />

          <Form.Item
            name="payStubsAvailable"
            label={
              <Typography.Text strong>
                Do {isCompletedBySponsor ? `they` : 'you'} have recent pay stubs?
              </Typography.Text>
            }
            rules={[
              { required: true, message: `Please specify if ${isCompletedBySponsor ? 'they' : 'you'} have pay stubs` },
            ]}
          >
            <RadioCards
              options={[
                {
                  label: 'Yes (from the past month)',
                  value: true,
                },
                {
                  label: 'No (just started within 30 days)',
                  value: false,
                },
              ]}
            />
          </Form.Item>

          {/* TODO: This is commented out and will come back at a later date */}
          {false && (
            <Card
              color="primary"
              className="mb-8"
              title={
                <span className="flex gap-3 items-center">
                  <Document size={24} className="mt-[-1px]" />
                  <div>Employment Verification</div>
                </span>
              }
            >
              <Typography.Text type="secondary" className="block mb-8">
                Since {isCompletedBySponsor ? 'your spouse' : 'you'} recently started this job and does not have pay
                stubs yet, please complete this verification form
              </Typography.Text>

              <Form.Item
                name="companyName"
                label={
                  <Typography.Text>
                    {isCompletedBySponsor ? "Your spouse's employer name" : 'Your employer name'}
                  </Typography.Text>
                }
                rules={[{ required: true, message: 'Employer name is required' }]}
              >
                <Input placeholder="Enter employer name" />
              </Form.Item>

              <Form.Item
                name="jobTitle"
                label={
                  <Typography.Text>
                    {isCompletedBySponsor ? "Your spouse's job title" : 'Your job title'}
                  </Typography.Text>
                }
                rules={[{ required: true, message: 'Job title is required' }]}
              >
                <Input placeholder="Enter job title" />
              </Form.Item>

              <Form.Item
                name="startDate"
                label={`When did ${isCompletedBySponsor ? 'your spouse' : 'you'} start this job?`}
                rules={[{ required: true, message: 'Start date is required' }]}
              >
                <DatePicker />
              </Form.Item>

              <Form.Item
                name="employerState"
                label="Employer's location (State)"
                rules={[{ required: true, message: 'Employer state is required' }]}
              >
                <AsyncSelect
                  placeholder="Select..."
                  ariaLabel="State"
                  optionsApiUrl="/countries/states/US"
                  getOptionLabel={(opt) => opt.name}
                  getOptionValue={(opt) => opt.code}
                  isOptionSelected={(option, [value]) => option?.code === value}
                  onChangeFormatter={(v) => v?.code}
                />
              </Form.Item>

              <Form.Item
                name="supervisorName"
                label={isCompletedBySponsor ? "Your spouse's supervisor's name" : "Your supervisor's name"}
                rules={[{ required: true, message: "Supervisor's name is required" }]}
              >
                <Input placeholder="Enter supervisor name" />
              </Form.Item>

              <Form.Item
                name="supervisorPhone"
                label="Supervisor's phone number"
                rules={[
                  {
                    required: true,
                    message: "Supervisor's phone number is required",
                  },
                  getPhoneNumberValidator('Invalid phone number'),
                ]}
                validateFirst
                className="mt-8 mb-0"
              >
                <PhoneNumber />
              </Form.Item>

              <Divider />

              <Alert
                message="Important"
                description="You must notify Child Care Aware of America immediately on the day that any employment details change, including pay rate, hours, or if employment ends. Failure to report changes immediately may result in overpayment that must be repaid."
                showIcon
                className="mt-4 mb-8"
              />

              <Form.Item
                name="certificationAccepted"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('You must accept the certification')),
                  },
                ]}
              >
                <Checkbox>
                  I certify that this employment information about {isCompletedBySponsor ? 'my spouse' : 'me'} is
                  accurate and complete. I understand that providing false information may subject me to prosecution
                  under the Uniform Code of Military Justice (UCMJ) and/or applicable federal laws. I understand that we
                  must provide pay stubs within 60 days to maintain eligibility, and that any overpayment resulting from
                  inaccurate information must be repaid.
                </Checkbox>
              </Form.Item>

              <Form.Item
                name="certificationSigned"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error('You must accept the electronic signature')),
                  },
                ]}
              >
                <Checkbox>
                  By checking this box, I electronically sign this Employment Verification form. I understand this
                  digital signature has the same legal effect and enforceability as a handwritten signature.
                </Checkbox>
              </Form.Item>
            </Card>
          )}

          <Divider />

          <Form.Item
            name="hoursPerWeek"
            label="Total hours per week"
            rules={[
              { required: true, message: 'Hours per week is required' },
              {
                validator: (_, value) =>
                  value > 100
                    ? Promise.reject(new Error('Please verify hours (over 14 hours/day, every day)'))
                    : Promise.resolve(),
              },
            ]}
          >
            <InputNumber size="large" min={1} max={200} addonAfter="hour(s)" controls={false} className="w-44" />
          </Form.Item>

          <Form.Item
            name="employerCount"
            label="Number of employers"
            rules={[{ required: true, message: 'Number of employers is required' }]}
          >
            <Select
              options={[
                {
                  title: '1 employer',
                  id: 1,
                },
                {
                  title: '2 employers',
                  id: 2,
                },
                {
                  title: '3 employers',
                  id: 3,
                },
                {
                  title: '4 employers',
                  id: 4,
                },
                {
                  title: '5 or more employers',
                  id: 5,
                },
              ]}
              getOptionValue={(v) => v.id}
              getOptionLabel={(opt) => opt.title}
              placeholder="Select..."
              ariaLabel="Number of employers"
            />
          </Form.Item>

          {payStubsAvailableFormValue === true &&
            typeof employerCountFormValue?.id === 'number' &&
            employerCountFormValue?.id === 1 && (
              <Form.Item
                name="payFrequency"
                label={
                  <Typography.Text strong>
                    How often{' '}
                    {isCompletedBySponsor
                      ? `is ${getOtherAdultFirstName(application?.additionalParents?.[0])}`
                      : 'are you'}{' '}
                    paid?
                  </Typography.Text>
                }
                rules={[{ required: true, message: 'Pay frequency is required' }]}
              >
                <RadioCards
                  options={[
                    {
                      label: 'Weekly',
                      value: 'WEEKLY',
                    },
                    {
                      label: 'Every two weeks (bi-weekly)',
                      value: 'BI_WEEKLY',
                    },
                    {
                      label: 'Twice a month (1st and 15th)',
                      value: 'TWICE_MONTHLY',
                    },
                    {
                      label: 'Monthly',
                      value: 'MONTHLY',
                    },
                  ]}
                />
              </Form.Item>
            )}

          {typeof employerCountFormValue?.id === 'number' && employerCountFormValue?.id !== 1 && (
            <Alert
              message="Multiple Jobs Documentation"
              description="You'll need to submit documentation (pay stubs or verification forms) for each employer during the verification step"
              showIcon
              className="mt-4 mb-8"
            />
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYER_DETAILS,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppSpouseEmployerDetails;
